/**
 * Article path mode UI (Task 32; approved mock .design/articles-path-mode, D-2…D-5, copy C-5…C-20).
 * Pure UI over `useArticlePathMode` (state, job driving, validation, errors and the `view` reading live in the hook):
 *  - Home page select (D-2) — rendered first, in the article-domain area, above the domain's own actions.
 *  - "Path on your main site" section — status line, Base URL + "Verify & enable" (decision #10: the server set),
 *    proxy rule tabs + robots.txt line once a valid base is typed (D-3), Disable (D-4 dialog), switch report (D-5).
 * Mounted only by `CustomDomainSection surface="article"` with an ACTIVE article domain (AC-035).
 * Focus never drops to <body>: the input stays focusable (read-only) while a request runs and takes focus on Verify
 * and after Disable; a failed Verify is announced (role="alert").
 */
import React, { useId, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Divider, InputAdornment, Skeleton, Stack, Typography } from '@mui/material';
import type { FormHelperTextProps } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
// Subpath imports: the ui-components root barrel is not jest-safe (import.meta via data-pickers).
import { StyledTextField } from '@modules/ui-components/forms/fields/StyledTextField';
import { StyledSelectDropdown, type SelectOption } from '@modules/ui-components/forms/fields/StyledSelectDropdown';
import { useArticlePathMode, type UseArticlePathModeResult } from '@modules/custom-domains/hooks/useArticlePathMode';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';
import { robotsSitemapLine } from '@modules/custom-domains/utils/proxySnippets';
import { PATH_MODE_COPY, clientValidationText, verifyErrorText } from '@modules/custom-domains/components/articlePathModeCopy';
import { CodeBlock, CopyButton, ProxySnippetTabs } from '@modules/custom-domains/components/ProxySnippetTabs';
import { ModeSwitchReport } from '@modules/custom-domains/components/ModeSwitchReport';
import { DisablePathModeDialog } from '@modules/custom-domains/components/DisablePathModeDialog';

const ids = CUSTOM_DOMAIN_TEST_IDS.baseUrl;

type LastAction = 'verify' | 'disable' | 'home' | null;

/** The "Path on your main site" section shell (also used by CustomDomainSection for the not-yet-active hint). */
export function ArticlePathSection({ children }: { children: React.ReactNode }) {
  const titleId = `custom-domain-path-section-title-${useId()}`;
  return (
    <Box component="section" aria-labelledby={titleId} sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <LanguageIcon color="action" />
        <Box>
          <Typography variant="subtitle1" component="h2" id={titleId} sx={{ fontWeight: 600 }}>{PATH_MODE_COPY.sectionTitle}</Typography>
          <Typography variant="body2" color="text.secondary">{PATH_MODE_COPY.sectionSubtitle}</Typography>
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

/** A URL that parses (the validator already accepted it); null otherwise. */
const parsableBase = (value: string | null | undefined): string | null => {
  if (!value) return null;
  try {
    new URL(value);
    return value;
  } catch {
    return null;
  }
};

const hostAndPath = (value: string): string => value.trim().replace(/^[a-z]+:\/\//i, '');

function HomeAliasSelect({ hook, articleHost, disabled, error, onChange }: {
  hook: UseArticlePathModeResult;
  articleHost: string;
  disabled: boolean;
  error: string | null;
  onChange(alias: string | null): void;
}) {
  const copy = PATH_MODE_COPY.home;
  const current = hook.state?.homeAlias ?? '';
  const options: SelectOption[] = [
    { value: '', label: copy.none },
    ...hook.liveArticles.map((article) => ({ value: article.alias, label: copy.option(article.alias, article.title) })),
    // Never a blank control: the stored alias stays an option even when it is no longer live.
    ...(current && !hook.liveArticles.some((article) => article.alias === current) ? [{ value: current, label: copy.option(current) }] : []),
  ];
  return (
    <StyledSelectDropdown
      fullWidth
      label={copy.label}
      value={current}
      options={options}
      disabled={disabled}
      error={Boolean(error)}
      errorMessage={error ?? undefined}
      helperText={copy.helper(articleHost)}
      onChange={(value) => onChange(value === '' ? null : String(value))}
      data-testid={CUSTOM_DOMAIN_TEST_IDS.homeAlias.select}
      sx={{ mb: 2, maxWidth: 520 }}
    />
  );
}

function RobotsLine({ publicBaseUrl }: { publicBaseUrl: string }) {
  const line = robotsSitemapLine(publicBaseUrl);
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>{PATH_MODE_COPY.robots.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{PATH_MODE_COPY.robots.intro}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}><CodeBlock testId={ids.robotsLine}>{line}</CodeBlock></Box>
        <CopyButton text={line} label={PATH_MODE_COPY.robots.copyLabel} testId={ids.robotsCopy} />
      </Stack>
    </Box>
  );
}

export interface ArticlePathModePanelProps {
  projectId: string;
  /** The active article domain, e.g. articles.client.com. */
  articleHost: string;
  /** The article domain's own actions (Remove Domain): rendered between the Home page select and the path section. */
  domainActions?: React.ReactNode;
}

export function ArticlePathModePanel({ projectId, articleHost, domainActions }: ArticlePathModePanelProps) {
  const hook = useArticlePathMode(projectId);
  const { state, loading, busy, error, clientError, view } = hook;
  const { job, running, activeBaseUrl, failedKind, failedCount } = view;
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<LastAction>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusInputAfterDialog = useRef(false);
  const uid = useId();
  const errorId = `custom-domain-base-url-error-${uid}`;
  const helpId = `custom-domain-base-url-help-${uid}`;
  const copy = PATH_MODE_COPY;
  const pathActive = activeBaseUrl !== null;

  // Errors: client validation (C-16) and a failed Verify sit under the input; Home page errors under the select;
  // anything else (load, disable, retry, a lost switch) is a panel alert.
  // A Verify error belongs to the value that was sent: editing the input hides it (it never moves elsewhere).
  const clientText = pathActive ? null : clientValidationText(clientError, draft);
  const verifyErrorOwnsInput = !pathActive && lastAction === 'verify';
  const verifyError = verifyErrorOwnsInput && error && draft === submitted ? verifyErrorText(error, submitted) : null;
  const inputError = clientText ?? verifyError;
  const homeError = lastAction === 'home' && error ? error.message : null;
  const panelError = error && !verifyErrorOwnsInput && lastAction !== 'home' ? error.message : null;

  const inputValue = activeBaseUrl ?? (running && job?.kind === 'set' ? job.publicBaseUrl ?? draft : draft);
  const snippetBase = running ? null : activeBaseUrl ?? (clientError === null && draft.trim() ? parsableBase(draft.trim()) : null);
  const canVerify = !busy && !running && draft.trim() !== '' && clientError === null;
  const focusInput = () => inputRef.current?.focus();

  const handleChange = (value: string) => {
    setDraft(value);
    hook.validateInput(value);
  };
  const handleVerify = async () => {
    if (!canVerify) return;
    setLastAction('verify');
    setSubmitted(draft);
    // The Verify button disables while the request runs: keep focus on the (read-only) input instead of <body>.
    focusInput();
    await hook.verifyAndEnable(draft);
    focusInput();
  };
  const handleRetry = () => {
    if (!state?.publicBaseUrl) return;
    setLastAction('verify');
    void hook.verifyAndEnable(state.publicBaseUrl);
  };
  // A failed Disable is healed by running it again (the server re-runs an unhealed clear).
  const handleRetryDisable = () => {
    setLastAction('disable');
    void hook.disable();
  };
  const handleConfirmDisable = () => {
    // The Disable button goes away with path mode: after the dialog closes, focus moves to the input.
    focusInputAfterDialog.current = true;
    setDisableOpen(false);
    setDraft('');
    setSubmitted(null);
    setLastAction('disable');
    void hook.disable();
  };
  const handleDialogExited = () => {
    if (!focusInputAfterDialog.current) return;
    focusInputAfterDialog.current = false;
    focusInput();
  };
  const handleHome = (alias: string | null) => {
    setLastAction('home');
    void hook.setHome(alias);
  };

  const republished = job ? job.results.filter((r) => r.status === 'republished').length : 0;
  const pathFailures = failedKind === 'set' ? failedCount : 0;
  const status = running && job
    ? (job.kind === 'clear' ? copy.status.switchingToSubdomain : copy.status.switchingToPath)(republished, job.total)
    : activeBaseUrl
      ? (pathFailures ? copy.status.failed(pathFailures) : copy.status.active(activeBaseUrl))
      : copy.status.subdomain;
  const statusSub = running
    ? copy.sub.running
    : activeBaseUrl
      ? (pathFailures ? copy.sub.failed(activeBaseUrl, pathFailures) : copy.sub.active(activeBaseUrl))
      : busy === 'verifying' && submitted
        ? copy.sub.verifying(hostAndPath(submitted))
        : copy.sub.subdomain(articleHost);
  const siteHost = activeBaseUrl ? new URL(activeBaseUrl).host : '';

  const helperProps = {
    id: inputError ? errorId : helpId,
    // A failed Verify is announced; live typing errors are not (they would interrupt every keystroke).
    role: verifyError ? 'alert' : undefined,
    'data-testid': inputError ? ids.error : undefined,
  } as Partial<FormHelperTextProps>;

  const pathBody = loading && !state ? (
    <Box>
      <Skeleton variant="text" width={240} height={28} />
      <Skeleton variant="rectangular" height={56} sx={{ mt: 1, borderRadius: 1, maxWidth: 560 }} />
    </Box>
  ) : (
    <Box data-testid={ids.panel}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        {copy.intro(snippetBase ?? (running ? parsableBase(job?.publicBaseUrl) : null) ?? copy.introExampleBase)}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {running && job && (
            <CircularProgress
              size={18}
              variant="determinate"
              value={job.total ? Math.round((republished / job.total) * 100) : 0}
              aria-label={`${republished} of ${job.total} articles`}
            />
          )}
          <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid={ids.status} aria-live="polite">{status}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">{statusSub}</Typography>
      </Box>

      {panelError && <Alert severity="error" sx={{ mb: 2 }}>{panelError}</Alert>}

      <Stack
        component="form"
        noValidate
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          void handleVerify();
        }}
      >
        <StyledTextField
          fullWidth
          label={copy.input.label}
          placeholder={copy.input.placeholder}
          value={inputValue}
          onChange={handleChange}
          error={Boolean(inputError)}
          // Read-only (never disabled) while a request or switch runs: it keeps focus and stays readable.
          readOnly={pathActive || busy === 'verifying' || running}
          helperText={inputError ?? (pathActive ? copy.input.readOnlyHelper : copy.input.helper)}
          aria-describedby={inputError ? errorId : helpId}
          FormHelperTextProps={helperProps}
          inputRef={inputRef}
          inputProps={{ 'data-testid': ids.input }}
          startAdornment={pathActive ? <InputAdornment position="start"><LanguageIcon fontSize="small" /></InputAdornment> : undefined}
          sx={{ maxWidth: { md: 560 }, minWidth: 0 }}
        />
        {!pathActive && (
          <Button
            type="submit"
            variant="contained"
            disabled={!canVerify}
            startIcon={busy === 'verifying' || running ? <CircularProgress size={16} color="inherit" /> : undefined}
            data-testid={ids.verifyBtn}
            sx={{ mt: { md: 1 }, flexShrink: 0, minWidth: 156, alignSelf: { xs: 'flex-start', md: 'auto' } }}
          >
            {busy === 'verifying' ? copy.buttons.verifying : running ? copy.buttons.republishing : copy.buttons.verify}
          </Button>
        )}
      </Stack>

      {pathActive && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {failedKind === 'set' && (
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRetry} disabled={Boolean(busy)}>
              {failedCount > 0 ? copy.buttons.retryFailed : copy.buttons.verify}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={busy === 'disabling' ? <CircularProgress size={16} color="inherit" /> : <LinkOffIcon />}
            onClick={() => setDisableOpen(true)}
            disabled={Boolean(busy)}
            data-testid={ids.disableBtn}
          >
            {copy.buttons.disable}
          </Button>
        </Stack>
      )}

      {/* After a failed Disable, running it again re-runs the switch on the server (site files, republish, related
          refresh); single articles can also be republished from the Library. */}
      {failedKind === 'clear' && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRetryDisable} disabled={Boolean(busy) || running}>
            {failedCount > 0 ? copy.buttons.retryFailed : copy.buttons.retrySwitch}
          </Button>
          {failedCount > 0 && (
            <Button variant="outlined" component={RouterLink} to={`/project/${projectId}/articles`} startIcon={<LibraryBooksIcon />}>
              {copy.buttons.openLibrary}
            </Button>
          )}
        </Stack>
      )}

      {job && <ModeSwitchReport job={job} articleHost={articleHost} liveArticles={hook.liveArticles} />}

      {!running && (
        <>
          <Divider sx={{ my: 3 }} />
          {snippetBase ? (
            <>
              <ProxySnippetTabs articleHost={articleHost} publicBaseUrl={snippetBase} />
              <RobotsLine publicBaseUrl={snippetBase} />
            </>
          ) : (
            <Alert severity="info">{copy.proxy.beforeTyping}</Alert>
          )}
        </>
      )}

      <DisablePathModeDialog
        open={disableOpen}
        articleHost={articleHost}
        siteHost={siteHost}
        onClose={() => setDisableOpen(false)}
        onConfirm={handleConfirmDisable}
        onExited={handleDialogExited}
      />
    </Box>
  );

  return (
    <>
      {state && (
        <HomeAliasSelect
          hook={hook}
          articleHost={articleHost}
          disabled={Boolean(busy) || running}
          error={homeError}
          onChange={handleHome}
        />
      )}
      {domainActions}
      <ArticlePathSection>{pathBody}</ArticlePathSection>
    </>
  );
}

export default ArticlePathModePanel;
